import { Injectable } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ContactApiModel, OutletApiModel } from './model/targeting-search-result.api.model';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { PrivateDataApiModel } from './model/private-data-api.model';
import { InteractionApiModel } from './model/interaction-api.model';
import { HandleModel } from '../../Discover/Targeting/models/handle.model';
import { TargetingQueryApi } from './model/targeting-search.api.model';
import { TargetingSearchResponseApiModel } from './model/targeting-search-response.api.model';
import { NeptuneApi } from '../genericApi/neptune.api';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { PatchPersonalization } from '../distributions/model/distribution-personalization.api.model';
import { SortOrderEnum } from '../../api/shared/enums/sort-order.enum';

@Injectable()
export class OutletsPublicApi {
    private usersSubscription?: Subscription;
    private locale: string = 'en-US';

    constructor(
        private serviceApi: NeptuneApi,
        private currentUserStateService: CurrentUserStateService,
    ) {
        this.usersSubscription = this.currentUserStateService.getDataReferenceLanguageTag().pipe(
            filter(x => !!x)
        ).subscribe(languageTag => {
            this.locale = languageTag;
        });
    }

    ngDestroy() {
        this.usersSubscription.unsubscribe();
    }

    public getOutlet(id: string): Observable<OutletApiModel> {
        return this.serviceApi.api<OutletApiModel>(
            `/outlets/public/${id}?locale=${this.locale}`,
            null,
            HttpVerbsEnum.GET
        );
    }


    public getPrimaryContact(id: string): Observable<ContactApiModel> {
        return this.serviceApi.api<ContactApiModel>(
            `/outlets/public/${id}/primarycontact?locale=${this.locale}`,
            null,
            HttpVerbsEnum.GET
        );
    }

    public updatePersonalizations(outletId: string, patchModel: PatchPersonalization) {
        const slug = `/outlets/public/${outletId}/personalizations`;
        return this.serviceApi.api<string>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    public setPrivateData(outletId: string, privateData: PrivateDataApiModel) {
        return this.serviceApi.api<string>(
            `/outlets/public/${outletId}/privatedata`,
            <PrivateDataApiModel>privateData,
            HttpVerbsEnum.PUT
        );
    }

    public getPrivateData(id: string): Observable<PrivateDataApiModel> {
        return this.serviceApi.api<PrivateDataApiModel>(
            `/outlets/public/${id}/privatedata?locale=${this.locale}`,
            null,
            HttpVerbsEnum.GET
        );
    }

    public getInteractions(entityId: string, rows: number): Observable<InteractionApiModel[]> {

        const slug: string = `/outlets/public/${entityId}/interactions?rows=${rows}&sortOrder=DESCENDING`;

        return this.serviceApi.api<InteractionApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    public setPreferredEmail(outletId: string, handle: HandleModel) {
        return this.serviceApi.api<string>(
            `/outlets/public/${outletId}/preferredemail?email=${handle.value}&isPrivateEmail=${handle.isPrivateData}`,
            null,
            HttpVerbsEnum.POST
        );
    }

    public addAllStaffToLists(ids: number[], outletUuid: string) {
        const slug: string = `/outlets/public/${outletUuid}/addallstafftolists`;

        return this.serviceApi.api<void>(
            slug,
            ids,
            HttpVerbsEnum.POST
        );
    }

    public searchAffiliates(
        search: TargetingQueryApi,
        outletUuid: string,
        start: number,
        rows: number,
        sortOrder: SortOrderEnum,
        sortField: string): Observable<TargetingSearchResponseApiModel> {
        const slug: string = `/outlets/public/${outletUuid}/affiliatesearch?locale=${this.locale}`;

        return this.serviceApi.api<TargetingSearchResponseApiModel>(
            slug,
            search,
            HttpVerbsEnum.POST,
            {
                rows: rows,
                start: start,
                sortBy: sortField,
                sortOrder: sortOrder
            }
        );
    }
}
